import {
    Colors,
    Icon,
    RadioGroup,
    Text,
    type IconProps,
    type OptionProps,
    type RadioGroupOption,
} from '@amelio/ui-library';

import type {
    OnSelectCompanyAttributeOption,
    SelectedFilters,
} from '@/hooks/filters/useCompanyAttributesFilters';
import type { CompanyAttributeMap } from '@/utils/company';
import type { MultiSelectAccent } from '@amelio/ui-library';

import { AudienceFilterList } from '@/components/AudienceFilterList/AudienceFilterList';
import { AudienceTile } from '@/components/AudienceTile/AudienceTile';
import {
    CycleAudienceType,
    type CycleAudienceTypeValue,
} from '@/constants/cycle-evaluation/CycleEvaluation';
import { useAmelioTranslations } from '@/hooks';

const AUDIENCE_ACCENT: MultiSelectAccent = 'violet';

export interface CycleAudienceSectionProps {
    isReadOnly?: boolean;
    isAudienceHydrated?: boolean;
    audienceType: CycleAudienceTypeValue;
    companyAttributes: OptionProps[];
    selectedCompanyAttributes: CompanyAttributeMap;
    filters: SelectedFilters;
    onAudienceTypeChange: (audienceType: CycleAudienceTypeValue) => void;
    onSelectCompanyAttribute: (value: string) => void;
    onSelectCompanyAttributeOption: (params: OnSelectCompanyAttributeOption) => void;
}

export const CycleAudienceSection = ({
    isReadOnly = false,
    isAudienceHydrated = true,
    audienceType,
    companyAttributes,
    selectedCompanyAttributes,
    filters,
    onAudienceTypeChange,
    onSelectCompanyAttribute,
    onSelectCompanyAttributeOption,
}: CycleAudienceSectionProps) => {
    const { t } = useAmelioTranslations();

    const options: RadioGroupOption[] = [
        {
            label: t('cycleEvaluationCycleInformation.audience.entireCompany.label'),
            value: CycleAudienceType.ENTIRE_COMPANY,
        },
        {
            label: t('cycleEvaluationCycleInformation.audience.specificTeams.label'),
            value: CycleAudienceType.FILTER_BY_ATTRIBUTES,
        },
    ];

    const getDescription = (value: string) =>
        value === CycleAudienceType.ENTIRE_COMPANY
            ? t('cycleEvaluationCycleInformation.audience.entireCompany.description')
            : t('cycleEvaluationCycleInformation.audience.specificTeams.description');

    const getIconName = (value: string): IconProps['name'] =>
        value === CycleAudienceType.ENTIRE_COMPANY ? 'building-building-4' : 'users-people';

    const isFilteringByAttributes = audienceType === CycleAudienceType.FILTER_BY_ATTRIBUTES;
    const hasConfiguredAttributes = companyAttributes.length > 0;

    return (
        <div data-testid={'cycle-audience-section'}>
            <RadioGroup
                className={'w-full max-w-[768px] [&>div]:gap-[12px]'}
                columns={2}
                options={options}
                name={'cycle-audience'}
                value={audienceType}
                readOnly={isReadOnly}
                renderOption={(optionProps) => (
                    <AudienceTile
                        {...optionProps}
                        description={getDescription(optionProps.option.value)}
                        iconName={getIconName(optionProps.option.value)}
                    />
                )}
                onChange={(value) => onAudienceTypeChange(value as CycleAudienceTypeValue)}
            />

            {isFilteringByAttributes && !hasConfiguredAttributes && (
                <div
                    className={'mt-6 flex items-start gap-2'}
                    role={'status'}
                    data-testid={'no-configured-attribute-message'}
                >
                    <Icon
                        name={'essential-info-circle'}
                        color={Colors.BASE_400}
                    />

                    <Text
                        size={'small'}
                        color={Colors.BASE_400}
                    >
                        {t('cycleEvaluationCycleInformation.audience.noAttributeConfigured')}
                    </Text>
                </div>
            )}

            {isFilteringByAttributes && hasConfiguredAttributes && isAudienceHydrated && (
                <AudienceFilterList
                    companyAttributes={companyAttributes}
                    selectedCompanyAttributes={selectedCompanyAttributes}
                    filters={filters}
                    accent={AUDIENCE_ACCENT}
                    isReadOnly={isReadOnly}
                    onSelectCompanyAttribute={onSelectCompanyAttribute}
                    onSelectCompanyAttributeOption={onSelectCompanyAttributeOption}
                />
            )}
        </div>
    );
};
